import { useEffect, useCallback } from 'react';
import { useGameStore } from '../store/gameStore';
import { gameEvents } from '../data/events';
import { getLifeStageForAge } from '../utils/gameHelpers';
import { PlayerStats } from '../types/game';

export const useTimeManagement = () => {
  const {
    isTimeRunning,
    timeSpeed,
    currentAge,
    currentMonth,
    currentStage,
    stats,
    status,
    money,
    activeHabits,
    habits,
    availableDecisions,
    mandatoryDecisions,
    addNotification,
    processEvent,
    updateStats,
    updateStatus,
    addMoney,
    addTimelineEvent,
    advanceAge,
    maxHabitSlots,
    currentHabitSlots,
    pauseTime,
    startTime,
    ensureDecisionsAvailable,
  } = useGameStore();

  // Smooth time progression effect
  useEffect(() => {
    if (!isTimeRunning) return;

    const interval = setInterval(() => {
      const state = useGameStore.getState();
      let newMonth = state.currentMonth + 1; // Always advance by exactly 1 month
      let newAge = state.currentAge;
      let newStage = state.currentStage;

      // Handle year progression
      if (newMonth > 12) {
        newAge += Math.floor(newMonth / 12);
        newMonth = newMonth % 12 || 12;
        newStage = getLifeStageForAge(newAge);
        
        // Update age and stage
        useGameStore.setState({
          currentAge: newAge,
          currentStage: newStage,
          currentMonth: newMonth,
        });

        // Add year progression notification (only for significant ages)
        if (newAge % 5 === 0 || newAge === 18 || newAge === 25 || newAge === 30 || newAge === 40 || newAge === 50 || newAge === 60 || newAge === 70 || newAge === 80 || newAge === 90) {
          addNotification({
            type: 'milestone',
            title: 'New Year',
            message: `You are now ${newAge} years old!`,
            read: false,
          });
        }

        // Check for habit slot unlocks
        const newMaxSlots = Math.min(8, Math.floor(newAge / 10) + 3);
        if (newMaxSlots > maxHabitSlots) {
          useGameStore.setState({ maxHabitSlots: newMaxSlots });
          addNotification({
            type: 'habit',
            title: 'New Habit Slot Unlocked',
            message: `You can now have up to ${newMaxSlots} active habits!`,
            read: false,
          });
        }

        // Special handling for reaching 18 - add first decisions and pause time
        if (newAge === 18 && state.currentAge < 18) {
          const earlyAdulthoodDecisions = state.decisions
            .filter(d => {
              const stageMatch = d.stage === 'early-adulthood';
              const ageMatch = (!d.minAge || newAge >= d.minAge) && (!d.maxAge || newAge <= d.maxAge);
              return stageMatch && ageMatch;
            })
            .map(d => d.id);
          
          // Prioritize ZUS decision and one other decision (limit to 2)
          const zusDecision = earlyAdulthoodDecisions.find(id => id === 'decision-zus');
          const otherDecisions = earlyAdulthoodDecisions.filter(id => id !== 'decision-zus');
          const firstDecisions = zusDecision ? 
            [zusDecision, ...otherDecisions.slice(0, 1)] : 
            earlyAdulthoodDecisions.slice(0, 2);
          
          useGameStore.setState({
            availableDecisions: firstDecisions,
            timeSpeed: 1, // Keep same speed (1 month per 200ms)
          });

          addNotification({
            type: 'milestone',
            title: 'You are now 18! 🎉',
            message: 'You are now an adult! Time to make your first important life decisions, including ZUS insurance.',
            read: false,
          });

          // Pause time to allow decision making
          pauseTime();
        }

        // Check for new decisions based on age (not just stage transitions)
        const currentDecisions = state.availableDecisions.length + state.mandatoryDecisions.length;
        if (currentDecisions < 2) { // Only add if we have less than 2 decisions
          const newDecisions = state.decisions
            .filter(d => {
              // Check if decision is available for current age
              const ageMatch = (!d.minAge || newAge >= d.minAge) && (!d.maxAge || newAge <= d.maxAge);
              // Check if decision is for current stage or any stage
              const stageMatch = d.stage === newStage || d.stage === 'any';
              // Check if not already completed or available
              const notCompleted = !state.completedDecisions.includes(d.id);
              const notAvailable = !state.availableDecisions.includes(d.id) && !state.mandatoryDecisions.includes(d.id);
              
              return ageMatch && stageMatch && notCompleted && notAvailable;
            })
            .slice(0, 2 - currentDecisions) // Limit to max 2 total decisions
            .map(d => d.id);
          
          if (newDecisions.length > 0) {
            useGameStore.setState({
              availableDecisions: [...state.availableDecisions, ...newDecisions]
            });
          }
        }

        // Ensure decisions are available for current age/stage
        ensureDecisionsAvailable();
      } else {
        useGameStore.setState({ currentMonth: newMonth });
      }

      // Process random events (reduced frequency for smoother experience)
      if (Math.random() < 0.05) { // 5% chance instead of 10%
        processRandomEvents();
      }
      
      // Process habit effects
      processHabitEffects();
      
      // Process work income
      processWorkIncome();

    }, 200); // Check every 200ms for faster progression

    return () => clearInterval(interval);
  }, [isTimeRunning, timeSpeed, currentAge, currentMonth, currentStage, stats, activeHabits, habits, addNotification, processEvent, updateStats, addTimelineEvent, advanceAge, maxHabitSlots, currentHabitSlots, pauseTime]);

  // Pause time when decisions are available (max 2 at once)
  useEffect(() => {
    const totalDecisions = availableDecisions.length + mandatoryDecisions.length;
    if (totalDecisions > 0 && isTimeRunning) {
      pauseTime();
    }
  }, [availableDecisions.length, mandatoryDecisions.length, isTimeRunning, pauseTime]);

  const processRandomEvents = useCallback(() => {
    const state = useGameStore.getState();
    
    // 10% chance of event per time tick
    if (Math.random() < 0.1) {
      const availableEvents = gameEvents.filter(event => {
        // Check age requirements
        if (event.minAge && state.currentAge < event.minAge) return false;
        if (event.maxAge && state.currentAge > event.maxAge) return false;
        
        // Check stat requirements
        if (event.requirements) {
          return Object.entries(event.requirements).every(([stat, value]) => 
            state.stats[stat as keyof PlayerStats] >= (value || 0)
          );
        }
        
        return true;
      });

      if (availableEvents.length > 0) {
        // Weight events by probability
        const totalProbability = availableEvents.reduce((sum, event) => sum + event.probability, 0);
        let random = Math.random() * totalProbability;
        
        for (const event of availableEvents) {
          random -= event.probability;
          if (random <= 0) {
            if (event.isInteractive) {
              // For interactive events, show notification and dispatch event
              addNotification({
                type: 'event',
                title: 'New Event!',
                message: event.title,
                read: false,
                action: () => {
                  window.dispatchEvent(new CustomEvent('showEvent', { detail: event }));
                }
              });
            } else {
              processEvent(event);
            }
            break;
          }
        }
      }
    }
  }, [processEvent]);

  const processHabitEffects = useCallback(() => {
    const state = useGameStore.getState();
    const habitEffects: Partial<PlayerStats> = {};
    
    state.activeHabits.forEach(habitId => {
      const habit = state.habits.find(h => h.id === habitId);
      if (habit && habit.isActive) {
        Object.entries(habit.statEffects).forEach(([key, value]) => {
          if (value !== undefined) {
            habitEffects[key as keyof PlayerStats] = (habitEffects[key as keyof PlayerStats] || 0) + value;
          }
        });
      }
    });

    if (Object.keys(habitEffects).length > 0) {
      updateStats(habitEffects);
    }
  }, [updateStats]);

  const processWorkIncome = useCallback(() => {
    const state = useGameStore.getState();
    const { stats, status, currentMonth } = state;
    
    // Calculate dynamic monthly expenses based on age and living situation
    let monthlyExpenses = 0;
    
    if (currentAge < 18) {
      // Children have no expenses - parents provide everything
      monthlyExpenses = 0;
    } else if (currentAge < 25) {
      // Young adults - basic expenses
      if (status.livingLocation === 'With Parents') {
        monthlyExpenses = 200; // Minimal expenses when living with parents
      } else if (status.livingLocation === 'Rented Apartment') {
        monthlyExpenses = 800; // Rent + basic living
      } else if (status.livingLocation === 'Owned Home') {
        monthlyExpenses = 400; // Mortgage + utilities
      } else {
        monthlyExpenses = 300; // Default for young adults
      }
    } else if (currentAge < 35) {
      // Young professionals
      if (status.livingLocation === 'With Parents') {
        monthlyExpenses = 300;
      } else if (status.livingLocation === 'Rented Apartment') {
        monthlyExpenses = 1200;
      } else if (status.livingLocation === 'Owned Home') {
        monthlyExpenses = 800;
      } else {
        monthlyExpenses = 600;
      }
    } else if (currentAge < 50) {
      // Middle-aged adults
      if (status.livingLocation === 'Rented Apartment') {
        monthlyExpenses = 1500;
      } else if (status.livingLocation === 'Owned Home') {
        monthlyExpenses = 1000;
      } else {
        monthlyExpenses = 1200;
      }
    } else {
      // Older adults
      if (status.livingLocation === 'Rented Apartment') {
        monthlyExpenses = 1800;
      } else if (status.livingLocation === 'Owned Home') {
        monthlyExpenses = 1200;
      } else {
        monthlyExpenses = 1500;
      }
    }

    // Add ZUS contribution if registered
    if (status.isZusRegistered) {
      monthlyExpenses += 200;
    }

    // Add pet expenses if you have a pet
    if (status.hasPet) {
      monthlyExpenses += 50;
    }

    // Check if monthly expenses increased and notify
    const previousExpenses = status.monthlyExpenses;
    if (monthlyExpenses > previousExpenses && previousExpenses > 0) {
      const increase = monthlyExpenses - previousExpenses;
      addNotification({
        type: 'money',
        title: 'Monthly Expenses Increased',
        message: `Your monthly expenses increased by $${increase.toLocaleString()}. New total: $${monthlyExpenses.toLocaleString()}`,
        read: false,
      });
    }

    // Update monthly expenses
    updateStatus({ monthlyExpenses });
    
    // Add income every month if employed
    if (status.hasJob) {
      // Base income from wealth score (0-100)
      const baseIncome = Math.floor((stats.wealth / 100) * 8000); // Up to $8000/month (increased)
      
      // Minimum income for young people (increased)
      const minIncome = currentAge < 25 ? 200 : 500;
      const actualIncome = Math.max(minIncome, baseIncome);
      
      addMoney(actualIncome);
      
      // Update monthly income tracking
      updateStatus({ monthlyIncome: actualIncome, lastPaycheck: Date.now() });
      
      // Monthly income processed silently (no notification)
    } else if (currentAge < 7) {
      // Babies get no income
      const allowance = 0;
      addMoney(allowance);
      updateStatus({ monthlyIncome: allowance, lastPaycheck: Date.now() });
    } else if (currentAge < 12) {
      // Young children get small allowance
      const allowance = 2;
      addMoney(allowance);
      updateStatus({ monthlyIncome: allowance, lastPaycheck: Date.now() });
    } else if (currentAge < 18) {
      // Teenagers get allowance from parents, but can earn more with jobs
      const baseAllowance = 20;
      let jobBonus = 0;
      
      // Check if they have completed teenage job decisions
      const completedDecisions = useGameStore.getState().completedDecisions;
      if (completedDecisions.includes('decision-teenage-job-1')) {
        jobBonus += 5; // Lawn mowing job - reduced from 15
      }
      if (completedDecisions.includes('decision-teenage-job-2')) {
        jobBonus += 8; // Store job - reduced from 25
      }
      
      const totalIncome = baseAllowance + jobBonus;
      addMoney(totalIncome);
      updateStatus({ monthlyIncome: totalIncome, lastPaycheck: Date.now() });
    } else if (currentAge < 25) {
      // Young adults get allowance if not employed, but can earn more with jobs
      const baseAllowance = 150;
      let jobBonus = 0;
      
      // Check if they have completed job decisions
      const completedDecisions = useGameStore.getState().completedDecisions;
      if (completedDecisions.includes('decision-first-job')) {
        jobBonus += 100; // First real job - reduced from 200
      }
      if (completedDecisions.includes('decision-part-time-job')) {
        jobBonus += 50; // Part-time job - reduced from 100
      }
      if (completedDecisions.includes('decision-internship')) {
        jobBonus += 40; // Internship - reduced from 80
      }
      
      const totalIncome = baseAllowance + jobBonus;
      addMoney(totalIncome);
      updateStatus({ monthlyIncome: totalIncome, lastPaycheck: Date.now() });
    }

    // Deduct monthly expenses (allow debt)
    if (monthlyExpenses > 0) {
      useGameStore.getState().spendMoney(monthlyExpenses);
      // Monthly expenses processed silently (no notification)
    }

    // Recalculate net income
    useGameStore.getState().calculateNetIncome();

    // Update debt tracking
    const currentMoney = useGameStore.getState().money;
    if (currentMoney < 0) {
      const debtAmount = Math.abs(currentMoney);
      updateStats({ debt: debtAmount });
      
        // Check for suicide at 200k debt (increased threshold)
        if (debtAmount >= 200000) {
        addNotification({
          type: 'event',
          title: 'Game Over - Suicide',
          message: `Your debt of $${debtAmount.toLocaleString()} became overwhelming. You couldn't bear the financial pressure anymore.`,
          read: false,
        });
        
        // Set game over
        useGameStore.setState({ gamePhase: 'dead', causeOfDeath: 'suicide' });
        pauseTime();
        return;
      }
      
      // Add stress based on debt amount
      const stressIncrease = Math.min(Math.floor(debtAmount / 1000), 50); // Max 50 stress
      updateStats({ stress: stressIncrease });
    } else {
      // No debt, reduce stress over time
      updateStats({ debt: 0, stress: Math.max(0, stats.stress - 1) });
    }
  }, [addMoney, updateStatus, addNotification, updateStats, currentAge, pauseTime]);

  return {
    isTimeRunning,
    timeSpeed,
    currentAge,
    currentMonth,
    currentStage,
  };
};
